import React, { useState, useEffect, useCallback } from "react";
import MyProspectsSkeleton from "../../components/layout/MyProspectsSkeleton";
import api from "../../api/api";
import { useTheme } from "../../hooks/useTheme";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";
import {
    UserPlus, Trash2, Send, Search, Users,
    Sparkles, Linkedin, Mail, Phone, Inbox, Calendar, MessageSquare,
    Archive, RotateCcw
} from "lucide-react";

export default function MyProspects() {
    const { isDarkMode } = useTheme();
    const { user } = useAuth();
    const navigate = useNavigate();
    const [prospects, setProspects] = useState([]);
    const [archivedProspects, setArchivedProspects] = useState([]);
    const [viewMode, setViewMode] = useState("active"); // "active" | "archived"
    const [replies, setReplies] = useState([]);
    const [repliesLoading, setRepliesLoading] = useState(false);
    const [loading, setLoading] = useState(true);
    const [showAddForm, setShowAddForm] = useState(false);
    const [actionLoading, setActionLoading] = useState(false);
    const [message, setMessage] = useState(null);
    const [searchTerm, setSearchTerm] = useState("");
    const [bookingLink, setBookingLink] = useState("");
    const [lastInviteUrl, setLastInviteUrl] = useState("");
    const prospectStages = ["New", "Contacted", "Replied", "Booked", "Converted"];

    // Add form state
    const [formData, setFormData] = useState({
        firstName: "",
        lastName: "",
        email: "",
        phone: "",
        linkedinUrl: "",
    });

    useEffect(() => {
        if (!user?.isCoach && !user?.isAdmin) navigate("/userdashboard");
    }, [user, navigate]);

    const fetchProspects = useCallback(async () => {
        try {
            setLoading(true);
            const res = await api.get("/coach/prospects");
            setProspects(res.data.prospects || []);
        } catch {
            setProspects([]);
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => { fetchProspects(); }, [fetchProspects]);

    const fetchArchivedProspects = useCallback(async () => {
        try {
            const res = await api.get("/coach/prospects/archived");
            setArchivedProspects(res.data.prospects || []);
        } catch {
            setArchivedProspects([]);
        }
    }, []);

    useEffect(() => {
        if (viewMode === "archived") fetchArchivedProspects();
    }, [viewMode, fetchArchivedProspects]);

    const fetchReplies = useCallback(async () => {
        try {
            setRepliesLoading(true);
            const res = await api.get("/coach/prospects/replies");
            setReplies(res.data.replies || []);
        } catch {
            setReplies([]);
        } finally {
            setRepliesLoading(false);
        }
    }, []);

    useEffect(() => { fetchReplies(); }, [fetchReplies]);

    const showMsg = (text, type = "success") => {
        setMessage({ text, type });
        setTimeout(() => setMessage(null), 4000);
    };

    const handleAdd = async (e) => {
        e.preventDefault();
        if (!formData.firstName.trim() || !formData.lastName.trim() || !formData.email.trim()) {
            showMsg("First name, last name, and email are required", "error");
            return;
        }
        const phoneDigits = formData.phone.replace(/\D/g, "");
        if (phoneDigits && (phoneDigits.length < 7 || phoneDigits.length > 15)) {
            showMsg("Phone number must be 7\u201315 digits", "error");
            return;
        }
        setActionLoading(true);
        try {
            await api.post("/coach/prospects", {
                firstName: formData.firstName.trim(),
                lastName: formData.lastName.trim(),
                email: formData.email.trim(),
                phone: formData.phone.trim(),
                linkedinUrl: formData.linkedinUrl.trim(),
            });
            showMsg("Prospect added successfully");
            setFormData({ firstName: "", lastName: "", email: "", phone: "", linkedinUrl: "" });
            setShowAddForm(false);
            fetchProspects();
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to add prospect", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleDelete = async (prospectId) => {
        if (!window.confirm("Archive this prospect? You can restore them later.")) return;
        setActionLoading(true);
        try {
            await api.delete(`/coach/prospects/${prospectId}`);
            setLastInviteUrl("");
            showMsg("Prospect archived");
            fetchProspects();
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to archive prospect", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleRestore = async (prospectId) => {
        setActionLoading(true);
        try {
            await api.post(`/coach/prospects/${prospectId}/restore`);
            showMsg("Prospect restored");
            fetchArchivedProspects();
            fetchProspects();
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to restore prospect", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleInvite = async (prospectId) => {
        setActionLoading(true);
        try {
            const res = await api.post(`/coach/prospects/${prospectId}/invite`, {
                bookingLink: bookingLink.trim() || undefined,
            });
            const inviteUrl = res.data?.prospect?.InviteUrl;
            if (inviteUrl) {
                setLastInviteUrl(inviteUrl);
                showMsg("Invitation prepared with booking link");
            } else {
                showMsg("Invitation sent!");
            }
            fetchProspects();
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to send invitation", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const handleStageUpdate = async (prospectId, stage) => {
        setActionLoading(true);
        try {
            await api.put(`/coach/prospects/${prospectId}/stage`, { stage });
            showMsg("Prospect stage updated");
            fetchProspects();
        } catch (err) {
            showMsg(err.response?.data?.error || "Failed to update stage", "error");
        } finally {
            setActionLoading(false);
        }
    };

    const activeList = viewMode === "archived" ? archivedProspects : prospects;
    const filtered = activeList.filter(p => {
        const term = searchTerm.toLowerCase();
        return (
            (p.FirstName || "").toLowerCase().includes(term) ||
            (p.LastName || "").toLowerCase().includes(term) ||
            (p.Email || "").toLowerCase().includes(term)
        );
    });

    const inputClass = `w-full border rounded-xl px-3 py-2.5 text-sm ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400" : "bg-white border-slate-300 text-slate-700 placeholder-slate-400"}`;
    const labelClass = `block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`;

    if (loading) return <MyProspectsSkeleton isDarkMode={isDarkMode} />;

    return (
        <div className={`min-h-screen ${isDarkMode ? "bg-surface" : "bg-surface"}`}>
            <div className="max-w-5xl mx-auto px-4 py-8">
                {/* Header */}
                <div className="rounded-2xl overflow-hidden mb-8 shadow-sm">
                    <div className={`relative px-6 py-8 ${isDarkMode ? "bg-gradient-to-r from-blue-900 via-indigo-900 to-violet-900" : "bg-gradient-to-r from-blue-600 via-indigo-600 to-violet-600"}`}>
                        <Sparkles className="absolute top-4 right-6 text-white/20" size={40} />
                        <div className="relative flex items-center gap-4">
                            <div className="w-14 h-14 rounded-2xl bg-white/20 backdrop-blur-sm flex items-center justify-center text-white shadow-lg border border-white/30">
                                <Users size={28} />
                            </div>
                            <div>
                                <h1 className="text-2xl font-extrabold tracking-tight text-white">My Prospects</h1>
                                <p className="text-sm text-white/70">Add potential students to your coaching database</p>
                            </div>
                        </div>
                    </div>
                </div>

                {/* Message toast */}
                {message && (
                    <div role="alert" className={`mb-5 flex items-center gap-3 p-3.5 rounded-xl text-sm font-medium ${
                        message.type === "error"
                            ? isDarkMode ? "bg-red-900/30 text-red-300 border border-red-800/50" : "bg-red-50 text-red-800 border border-red-200"
                            : isDarkMode ? "bg-primary-900/30 text-primary-300 border border-primary-800/50" : "bg-primary-50 text-primary-800 border border-primary-200"
                    }`}>
                        {message.text}
                    </div>
                )}

                {lastInviteUrl && (
                    <div className={`mb-5 p-3 rounded-xl text-sm border ${isDarkMode ? "bg-sky-900/20 border-sky-800 text-sky-200" : "bg-sky-50 border-sky-200 text-sky-700"}`}>
                        <div className="font-semibold mb-1">Booking Invite URL</div>
                        <a href={lastInviteUrl} target="_blank" rel="noopener noreferrer" className="underline break-all">
                            {lastInviteUrl}
                        </a>
                    </div>
                )}

                {/* Actions Bar */}
                <div className="flex flex-col sm:flex-row gap-3 mb-6">
                    <div className="relative flex-1">
                        <Search className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDarkMode ? "text-slate-400" : "text-slate-400"}`} size={18} />
                        <input
                            type="text"
                            placeholder="Search prospects..."
                            value={searchTerm}
                            onChange={e => setSearchTerm(e.target.value)}
                            className={`w-full pl-10 pr-4 py-2.5 rounded-xl border text-sm ${isDarkMode ? "bg-slate-800 border-slate-700 text-slate-200 placeholder-slate-400" : "bg-white border-slate-200 text-slate-700 placeholder-slate-400"}`}
                        />
                    </div>
                    <div className={`inline-flex rounded-xl border p-1 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200"}`}>
                        <button
                            type="button"
                            onClick={() => setViewMode("active")}
                            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode === "active" ? (isDarkMode ? "bg-slate-700 text-white" : "bg-slate-900 text-white") : (isDarkMode ? "text-slate-300 hover:bg-slate-700/50" : "text-slate-600 hover:bg-slate-100")}`}
                        >
                            Active
                        </button>
                        <button
                            type="button"
                            onClick={() => setViewMode("archived")}
                            className={`flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${viewMode === "archived" ? (isDarkMode ? "bg-slate-700 text-white" : "bg-slate-900 text-white") : (isDarkMode ? "text-slate-300 hover:bg-slate-700/50" : "text-slate-600 hover:bg-slate-100")}`}
                            title="View archived prospects"
                        >
                            <Archive size={14} />
                            Archived
                        </button>
                    </div>
                    {viewMode === "active" && (
                        <button
                            onClick={() => setShowAddForm(!showAddForm)}
                            className="flex items-center gap-2 px-4 py-2.5 rounded-xl bg-primary-600 text-white font-semibold text-sm hover:bg-primary-700 transition-colors shadow-sm"
                        >
                            <UserPlus size={16} />
                            Add Prospect
                        </button>
                    )}
                </div>

                <div className={`rounded-2xl border p-4 mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                    <label className={`block text-sm font-medium mb-1 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                        External Calendar Link (Optional)
                    </label>
                    <input
                        type="url"
                        placeholder="https://calendly.com/your-name/intro-call"
                        value={bookingLink}
                        onChange={e => setBookingLink(e.target.value)}
                        className={`w-full border rounded-xl px-3 py-2.5 text-sm ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-200 placeholder-slate-400" : "bg-white border-slate-300 text-slate-700 placeholder-slate-400"}`}
                    />
                    <p className={`text-xs mt-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                        If left blank, invites use a built-in Pono Pilot booking request page (no third-party tool required).
                    </p>
                </div>

                {/* Add Prospect Form */}
                {showAddForm && (
                    <div className={`rounded-2xl border p-6 mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                        <h3 className={`text-lg font-bold mb-4 ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Add New Prospect</h3>
                        <form onSubmit={handleAdd} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                            <div>
                                <label className={labelClass}>First Name *</label>
                                <input type="text" className={inputClass} value={formData.firstName} onChange={e => setFormData({ ...formData, firstName: e.target.value })} required />
                            </div>
                            <div>
                                <label className={labelClass}>Last Name *</label>
                                <input type="text" className={inputClass} value={formData.lastName} onChange={e => setFormData({ ...formData, lastName: e.target.value })} required />
                            </div>
                            <div>
                                <label className={labelClass}>Email *</label>
                                <input type="email" className={inputClass} value={formData.email} onChange={e => setFormData({ ...formData, email: e.target.value })} required />
                            </div>
                            <div>
                                <label className={labelClass}>Phone</label>
                                <input
                                    type="tel"
                                    inputMode="tel"
                                    className={inputClass}
                                    value={formData.phone}
                                    maxLength={20}
                                    pattern="^[0-9+()\-\s]*$"
                                    placeholder="e.g. +1 555 123 4567"
                                    onChange={e => {
                                        // Allow digits, +, (), -, spaces only; cap at 20 chars, 15 digits.
                                        const raw = e.target.value.replace(/[^0-9+()\-\s]/g, "");
                                        const digitCount = raw.replace(/\D/g, "").length;
                                        if (digitCount > 15) return;
                                        setFormData({ ...formData, phone: raw.slice(0, 20) });
                                    }}
                                />
                            </div>
                            <div className="sm:col-span-2">
                                <label className={labelClass}>LinkedIn Profile URL</label>
                                <input type="url" className={inputClass} placeholder="https://linkedin.com/in/..." value={formData.linkedinUrl} onChange={e => setFormData({ ...formData, linkedinUrl: e.target.value })} />
                            </div>
                            <div className="sm:col-span-2 flex gap-2 justify-end">
                                <button type="button" onClick={() => setShowAddForm(false)} className={`px-4 py-2 rounded-xl text-sm font-medium ${isDarkMode ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}>Cancel</button>
                                <button type="submit" disabled={actionLoading} className="px-4 py-2 rounded-xl text-sm font-medium bg-primary-600 text-white hover:bg-primary-700 disabled:opacity-50">
                                    {actionLoading ? "Adding..." : "Add Prospect"}
                                </button>
                            </div>
                        </form>
                    </div>
                )}

                {/* Prospect Replies */}
                <div className={`rounded-2xl border mb-6 ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                    <div className={`flex items-center justify-between px-5 py-4 border-b ${isDarkMode ? "border-slate-700" : "border-slate-100"}`}>
                        <div className="flex items-center gap-3">
                            <div className={`w-9 h-9 rounded-xl flex items-center justify-center ${isDarkMode ? "bg-indigo-900/40 text-indigo-300" : "bg-indigo-50 text-indigo-600"}`}>
                                <Inbox size={18} />
                            </div>
                            <div>
                                <h3 className={`text-base font-bold ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>Prospect Replies</h3>
                                <p className={`text-xs ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                    Booking requests submitted by prospects via your invite link
                                </p>
                            </div>
                        </div>
                        <div className="flex items-center gap-2">
                            <span className={`text-xs font-semibold px-2.5 py-1 rounded-full ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                                {replies.length}
                            </span>
                            <button
                                type="button"
                                onClick={fetchReplies}
                                disabled={repliesLoading}
                                className={`text-xs font-semibold px-3 py-1.5 rounded-lg ${isDarkMode ? "bg-slate-700 text-slate-200 hover:bg-slate-600" : "bg-slate-100 text-slate-700 hover:bg-slate-200"} disabled:opacity-50`}
                            >
                                {repliesLoading ? "Refreshing..." : "Refresh"}
                            </button>
                        </div>
                    </div>
                    {repliesLoading ? (
                        <div className="flex items-center justify-center py-10">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-indigo-500" />
                        </div>
                    ) : replies.length === 0 ? (
                        <div className="px-5 py-8 text-center">
                            <MessageSquare className={`mx-auto mb-2 ${isDarkMode ? "text-slate-600" : "text-slate-300"}`} size={28} />
                            <p className={`text-sm font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                No replies yet
                            </p>
                            <p className={`text-xs mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                Replies will appear here when a prospect submits a booking request from their invite link.
                            </p>
                        </div>
                    ) : (
                        <ul className={`divide-y ${isDarkMode ? "divide-slate-700" : "divide-slate-100"}`}>
                            {replies.map((r) => {
                                const slots = (r.PreferredSlots || "").split("\n").map(s => s.trim()).filter(Boolean);
                                const replierName = r.RequesterName || `${r.FirstName || ""} ${r.LastName || ""}`.trim() || "Prospect";
                                const replierEmail = r.RequesterEmail || r.ProspectEmail;
                                return (
                                    <li key={r.BookingRequestID} className="px-5 py-4">
                                        <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                                            <div className="min-w-0 flex-1">
                                                <div className="flex items-center gap-2 flex-wrap">
                                                    <p className={`font-semibold text-sm ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                                        {replierName}
                                                    </p>
                                                    {replierEmail && (
                                                        <a href={`mailto:${replierEmail}`} className={`text-xs inline-flex items-center gap-1 ${isDarkMode ? "text-indigo-300 hover:text-indigo-200" : "text-indigo-600 hover:text-indigo-700"}`}>
                                                            <Mail size={12} /> {replierEmail}
                                                        </a>
                                                    )}
                                                </div>
                                                {slots.length > 0 && (
                                                    <div className="mt-2">
                                                        <p className={`text-xs font-semibold mb-1 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>
                                                            <Calendar size={12} className="inline -mt-0.5 mr-1" />
                                                            Preferred times
                                                        </p>
                                                        <ul className={`text-xs space-y-0.5 ${isDarkMode ? "text-slate-300" : "text-slate-700"}`}>
                                                            {slots.map((slot, i) => (
                                                                <li key={i} className="pl-4">&bull; {slot}</li>
                                                            ))}
                                                        </ul>
                                                    </div>
                                                )}
                                                {r.Notes && (
                                                    <div className={`mt-2 rounded-lg p-2.5 text-xs ${isDarkMode ? "bg-slate-900/60 text-slate-300" : "bg-slate-50 text-slate-700"}`}>
                                                        <p className={`font-semibold mb-0.5 ${isDarkMode ? "text-slate-400" : "text-slate-500"}`}>Notes</p>
                                                        {r.Notes}
                                                    </div>
                                                )}
                                            </div>
                                            <div className="text-right sm:min-w-[140px]">
                                                <p className={`text-[11px] ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                                                    {r.CreatedOn ? new Date(r.CreatedOn).toLocaleString() : ""}
                                                </p>
                                            </div>
                                        </div>
                                    </li>
                                );
                            })}
                        </ul>
                    )}
                </div>

                {/* Prospects Table */}
                {filtered.length === 0 ? (
                    <div className={`text-center py-16 rounded-2xl border ${isDarkMode ? "bg-slate-800/50 border-slate-700" : "bg-white border-slate-200"}`}>
                        <Users className={`mx-auto mb-3 ${isDarkMode ? "text-slate-500" : "text-slate-300"}`} size={48} />
                        <p className={`font-medium ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                            {searchTerm ? "No prospects match your search" : "No prospects yet"}
                        </p>
                        <p className={`text-sm mt-1 ${isDarkMode ? "text-slate-500" : "text-slate-400"}`}>
                            Click "Add Prospect" to get started
                        </p>
                    </div>
                ) : (
                    <div className={`rounded-2xl border overflow-hidden ${isDarkMode ? "bg-slate-800 border-slate-700" : "bg-white border-slate-200 shadow-sm"}`}>
                        <div className="overflow-x-auto">
                            <table className="w-full text-sm">
                                <thead>
                                    <tr className={isDarkMode ? "bg-slate-700/50" : "bg-slate-50"}>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>NAME</th>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>EMAIL</th>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>PHONE</th>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>LINKEDIN</th>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>STAGE</th>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>STATUS</th>
                                        <th className={`text-left px-4 py-3 font-semibold ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>ACTIONS</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filtered.map(p => (
                                        <tr key={p.ProspectID} className={`border-t ${isDarkMode ? "border-slate-700 hover:bg-slate-700/30" : "border-slate-100 hover:bg-slate-50"}`}>
                                            <td className={`px-4 py-3 font-medium ${isDarkMode ? "text-slate-100" : "text-slate-900"}`}>
                                                {p.FirstName} {p.LastName}
                                            </td>
                                            <td className={`px-4 py-3 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                                <span className="flex items-center gap-1.5"><Mail size={14} /> {p.Email}</span>
                                            </td>
                                            <td className={`px-4 py-3 ${isDarkMode ? "text-slate-300" : "text-slate-600"}`}>
                                                {p.Phone ? <span className="flex items-center gap-1.5"><Phone size={14} /> {p.Phone}</span> : "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                {p.LinkedInUrl ? (
                                                    <a href={p.LinkedInUrl} target="_blank" rel="noopener noreferrer" className="text-blue-500 hover:text-blue-400 flex items-center gap-1.5">
                                                        <Linkedin size={14} /> Profile
                                                    </a>
                                                ) : "—"}
                                            </td>
                                            <td className="px-4 py-3">
                                                <select
                                                    value={p.Stage || "New"}
                                                    onChange={(e) => handleStageUpdate(p.ProspectID, e.target.value)}
                                                    className={`rounded-lg border px-2 py-1 text-xs ${isDarkMode ? "bg-slate-700 border-slate-600 text-slate-200" : "bg-white border-slate-300 text-slate-700"}`}
                                                >
                                                    {prospectStages.map((stage) => (
                                                        <option key={stage} value={stage}>{stage}</option>
                                                    ))}
                                                </select>
                                            </td>
                                            <td className="px-4 py-3">
                                                {p.InvitedOn ? (
                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-blue-900/40 text-blue-300" : "bg-blue-50 text-blue-700"}`}>
                                                        <Send size={12} /> Invited
                                                    </span>
                                                ) : (
                                                    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium ${isDarkMode ? "bg-slate-700 text-slate-300" : "bg-slate-100 text-slate-600"}`}>
                                                        New
                                                    </span>
                                                )}
                                            </td>
                                            <td className="px-4 py-3">
                                                <div className="flex items-center gap-2">
                                                    {viewMode === "archived" ? (
                                                        <button
                                                            onClick={() => handleRestore(p.ProspectID)}
                                                            disabled={actionLoading}
                                                            title="Restore prospect"
                                                            className="p-1.5 rounded-lg text-primary-500 hover:bg-primary-500/10 transition-colors"
                                                        >
                                                            <RotateCcw size={16} />
                                                        </button>
                                                    ) : (
                                                        <>
                                                            {!p.InvitedOn && (
                                                                <button
                                                                    onClick={() => handleInvite(p.ProspectID)}
                                                                    disabled={actionLoading}
                                                                    title="Send invitation"
                                                                    className="p-1.5 rounded-lg text-primary-500 hover:bg-primary-500/10 transition-colors"
                                                                >
                                                                    <Send size={16} />
                                                                </button>
                                                            )}
                                                            <button
                                                                onClick={() => handleDelete(p.ProspectID)}
                                                                disabled={actionLoading}
                                                                title="Archive prospect"
                                                                className="p-1.5 rounded-lg text-red-500 hover:bg-red-500/10 transition-colors"
                                                            >
                                                                <Trash2 size={16} />
                                                            </button>
                                                        </>
                                                    )}
                                                </div>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
